/**
 * Cabin-class matrix helpers for the desktop fare-family Modal (§9.1).
 *
 * Ported/adapted from flights-pwa `src/utils/flights/cabinTabs.ts` (UIENG-7438)
 * so both platforms group/price/select cabins the same way against the shared
 * `/flight/fetch-interstitial-page` contract.
 *
 * Tab identity = `displayCabinType` (may be a sub-product like INDIGO_UPFRONT).
 * Theme + RT same-type rules = `cabinType` (always one of the four cabins).
 *
 * All BE-driven behaviour short-circuits to a client-side, best-effort grouping
 * when a subTravelOption carries no `cabinClassSummary` yet — callers must still
 * treat `hasCabinMatrix` (derived from `Boolean(cabinClassSummary)`) as the gate
 * for whether to filter the rendered fare list.
 */
import { INDIGO_UPFRONT } from '@/constants/addons'
import {
    CABIN_ORDER,
    CabinClassType,
    CabinTabId,
    DISPLAY_CABIN_THEME,
    FareCabinTabId,
    INDIGO_UPFRONT_CABIN,
} from '@/constants/flights/cabinClass'
import { CabinClassSummary } from '@/types/PremiumEconomyApi'

export type CabinFareLike = {
    fareId?: string
    cabinType?: string | null
    /** Tab bucket — airline sub-products (e.g. INDIGO_UPFRONT) live here. */
    displayCabinType?: string | null
    /** Legacy alias some payloads still send instead of displayCabinType. */
    displayCabinClass?: string | null
    fareCategory?: string | null
    fareGroup?: string
    fareFamilyType?: string
    fareCardMessage?: { title?: string }
    pricing?: {
        totalPricing?: { totalPrice?: number; strikeOffDetails?: { displayPrice?: number; strikeOffPrice?: number } }
        perAdultPricing?: { totalPrice?: number; strikeOffDetails?: { displayPrice?: number; strikeOffPrice?: number } }
    }
    totalFareAmount?: number
    [key: string]: any
}

type CabinClassSummaryEntry = {
    minCabinPrice?: number | null
    cabinFareIds?: string[]
    cabinPromoBanner?: unknown
}

type CabinClassSummaryLike = Record<string, CabinClassSummaryEntry> | null | undefined

const normalizeCabinValue = (cabin?: string | null): string | undefined => {
    if (!cabin) return undefined
    const normalized = String(cabin).trim().toUpperCase().replace(/[\s-]+/g, '_')
    if (!normalized) return undefined
    return normalized === 'FIRST_CLASS' ? 'FIRST' : normalized
}

const normalizeCabin = (cabin?: string | null): FareCabinTabId | undefined => {
    const key = normalizeCabinValue(cabin)
    return key && (CABIN_ORDER as string[]).includes(key) ? (key as FareCabinTabId) : undefined
}

/** BE `cabinType` first (authoritative), falls back to legacy `fareCategory`. */
export const normalizeCabinType = (fare?: CabinFareLike | null): CabinClassType | undefined =>
    normalizeCabin(fare?.cabinType) || normalizeCabin(fare?.fareCategory)

/**
 * Legacy branded-title detector (addons / seat mandate). Prefer `displayCabinType`
 * for tab membership — do not fold Up Front into PE.
 */
export const isIndigoUpfrontFare = (fare?: CabinFareLike | null): boolean => {
    if (normalizeCabinValue(fare?.displayCabinType || fare?.displayCabinClass) === INDIGO_UPFRONT_CABIN) {
        return true
    }
    return (fare?.fareCardMessage?.title || '').trim().toUpperCase() === INDIGO_UPFRONT
}

/**
 * Cabin that drives theming — always one of the four cabins. Prefers interstitial
 * `cabinType`, falling back to search-response `fareCategory`. Defaults to ECONOMY
 * so unresolved fares keep the neutral theme.
 */
export const resolveThemeCabin = (fare?: CabinFareLike | null): FareCabinTabId | undefined => {
    if (fare == null) return undefined
    return normalizeCabin(fare.cabinType) || normalizeCabin(fare.fareCategory) || 'ECONOMY'
}

/**
 * Tab a fare belongs to. `displayCabinType` splits the board so an airline
 * sub-product (Indigo Up Front) gets its own tab while still wearing its
 * `cabinType` theme. Falls back to theme cabin when BE hasn't sent a display cabin.
 */
export const resolveDisplayCabin = (fare?: CabinFareLike | null): CabinTabId | undefined => {
    if (fare == null) return undefined
    return (
        normalizeCabinValue(fare.displayCabinType) ||
        normalizeCabinValue(fare.displayCabinClass) ||
        resolveThemeCabin(fare)
    )
}

/**
 * Theme a tab wears, read off the `cabinType` of the fares inside it. Empty tabs
 * fall back to DISPLAY_CABIN_THEME / the tab id itself so a sub-product stays
 * neutral rather than borrowing PE chrome.
 */
export const getCabinThemeForTab = (
    faresArray: CabinFareLike[] = [],
    cabin?: CabinTabId
): FareCabinTabId | undefined => {
    if (!cabin) return undefined
    const fareInTab = faresArray.find(fare => resolveDisplayCabin(fare) === cabin)
    return (
        (fareInTab ? resolveThemeCabin(fareInTab) : undefined) ||
        DISPLAY_CABIN_THEME[cabin] ||
        normalizeCabin(cabin)
    )
}

/**
 * Canonical cabin order, with each cabin's sub-product tabs trailing it —
 * Economy | Indigo Upfront | Premium Economy | …
 */
const sortCabinTabs = (cabins: CabinTabId[], faresArray: CabinFareLike[]): CabinTabId[] =>
    [...cabins].sort((a, b) => {
        const themeA = getCabinThemeForTab(faresArray, a)
        const themeB = getCabinThemeForTab(faresArray, b)
        const rankA = themeA ? CABIN_ORDER.indexOf(themeA) : CABIN_ORDER.length
        const rankB = themeB ? CABIN_ORDER.indexOf(themeB) : CABIN_ORDER.length
        if (rankA !== rankB) return rankA - rankB
        const canonicalA = normalizeCabin(a) ? 0 : 1
        const canonicalB = normalizeCabin(b) ? 0 : 1
        if (canonicalA !== canonicalB) return canonicalA - canonicalB
        return a.localeCompare(b)
    })

export const groupFaresByCabin = (
    faresArray: CabinFareLike[] = []
): Record<CabinTabId, CabinFareLike[]> => {
    const grouped: Record<string, CabinFareLike[]> = {}
    faresArray.forEach(fare => {
        const cabin = resolveDisplayCabin(fare)
        if (!cabin) return
        if (!grouped[cabin]) grouped[cabin] = []
        grouped[cabin].push(fare)
    })
    return grouped
}

export const filterFaresByCabin = (
    faresArray: CabinFareLike[] = [],
    cabin: CabinTabId,
    // Kept for call-site compatibility; membership is displayCabinType-driven.
    _cabinClassSummary?: CabinClassSummary | null
): CabinFareLike[] => faresArray.filter(fare => resolveDisplayCabin(fare) === cabin)

/**
 * Fares are the only source that knows about display cabins; cabinClassSummary
 * keys only stand in while fares are missing.
 */
export const getAvailableCabins = ({
    cabinClassSummary,
    faresArray = [],
}: {
    cabinClassSummary?: CabinClassSummaryLike
    faresArray?: CabinFareLike[]
}): CabinTabId[] => {
    const grouped = groupFaresByCabin(faresArray)
    const fromFares = Object.keys(grouped)
    if (fromFares.length > 0) return sortCabinTabs(fromFares, faresArray)
    if (cabinClassSummary && typeof cabinClassSummary === 'object') {
        const fromSummary = Object.keys(cabinClassSummary).filter(cabin => {
            const entry = cabinClassSummary[cabin]
            return !!entry && Array.isArray(entry.cabinFareIds) && entry.cabinFareIds.length > 0
        })
        return sortCabinTabs(fromSummary, faresArray)
    }
    return []
}

const isEmptyValue = (value: unknown): boolean =>
    value == null ||
    (typeof value === 'string' && value === '') ||
    (typeof value === 'object' && Object.keys(value as object).length === 0)

const getFareDisplayPrice = (
    fare: CabinFareLike | null | undefined,
    showPerAdultPricing: boolean
): number | undefined => {
    const pricingObject = showPerAdultPricing ? fare?.pricing?.perAdultPricing : fare?.pricing?.totalPricing
    const { totalPrice, strikeOffDetails } = pricingObject || {}
    const displayPrice = !isEmptyValue(strikeOffDetails?.strikeOffPrice) ? strikeOffDetails?.displayPrice : totalPrice
    if (typeof displayPrice === 'number' && !Number.isNaN(displayPrice)) return displayPrice
    // Itinerary FARE_CARDS use totalFareAmount (no pricing.* shape).
    const itinAmount = fare?.totalFareAmount
    return typeof itinAmount === 'number' && !Number.isNaN(itinAmount) ? itinAmount : undefined
}

/**
 * A summary entry keyed by cabinType can span every fare of that cabin — wider
 * than a sub-product tab. Trust its min only when the entry and the tab cover
 * the same fares.
 */
const summaryCoversTab = (
    entry: CabinClassSummaryEntry | undefined,
    tabFares: CabinFareLike[]
): boolean => {
    if (!tabFares.length) return true
    const summaryFareIds = Array.isArray(entry?.cabinFareIds) ? entry!.cabinFareIds! : []
    if (!summaryFareIds.length) return false
    const tabFareIds = new Set(tabFares.map(fare => fare?.fareId))
    return summaryFareIds.every(fareId => tabFareIds.has(fareId))
}

/** Tab subtitle price. Prefers BE `minCabinPrice` when summaryCoversTab; else client min. */
export const getMinPriceByCabin = (
    faresArray: CabinFareLike[] = [],
    cabinClassSummary?: CabinClassSummaryLike,
    showPerAdultPricing = false
): Partial<Record<CabinTabId, number>> => {
    const grouped = groupFaresByCabin(faresArray)
    const cabins = new Set<CabinTabId>([...Object.keys(grouped), ...Object.keys(cabinClassSummary || {})])
    const prices: Partial<Record<CabinTabId, number>> = {}
    cabins.forEach(cabin => {
        const tabFares = grouped[cabin] || []
        const summaryEntry = cabinClassSummary?.[cabin]
        const summaryMin = summaryEntry?.minCabinPrice
        const hasSummaryMin = typeof summaryMin === 'number' && !Number.isNaN(summaryMin)
        if (hasSummaryMin && summaryCoversTab(summaryEntry, tabFares)) {
            prices[cabin] = Math.round(summaryMin as number)
            return
        }
        const cabinFarePrices = tabFares
            .map(fare => getFareDisplayPrice(fare, showPerAdultPricing))
            .filter((price): price is number => typeof price === 'number')
        if (cabinFarePrices.length) prices[cabin] = Math.round(Math.min(...cabinFarePrices))
        else if (hasSummaryMin) prices[cabin] = Math.round(summaryMin as number)
    })
    return prices
}

/**
 * Cheapest fare within a *display* cabin tab (e.g. upgrade CTA auto-select).
 */
export const getCheapestFareForCabin = (
    faresArray: CabinFareLike[] = [],
    cabin: CabinTabId,
    cabinClassSummary?: CabinClassSummary | null,
    showPerAdultPricing = false
): CabinFareLike | undefined => {
    const cabinFares = filterFaresByCabin(faresArray, cabin, cabinClassSummary)
    if (!cabinFares.length) return undefined
    return cabinFares.reduce<CabinFareLike | undefined>((cheapest, fare) => {
        if (!cheapest) return fare
        const price = getFareDisplayPrice(fare, showPerAdultPricing)
        const cheapestPrice = getFareDisplayPrice(cheapest, showPerAdultPricing)
        if (price == null) return cheapest
        if (cheapestPrice == null) return fare
        return price < cheapestPrice ? fare : cheapest
    }, undefined)
}

/**
 * Cheapest fare that *flies* in a theme cabin (RT same-type pairing).
 * Up Front and Economy both resolve to ECONOMY here.
 */
export const getCheapestFareForThemeCabin = (
    faresArray: CabinFareLike[] = [],
    cabin: FareCabinTabId,
    showPerAdultPricing = false
): CabinFareLike | undefined => {
    const matching = faresArray.filter(fare => resolveThemeCabin(fare) === cabin)
    if (!matching.length) return undefined
    return matching.reduce<CabinFareLike | undefined>((cheapest, fare) => {
        if (!cheapest) return fare
        const price = getFareDisplayPrice(fare, showPerAdultPricing)
        const cheapestPrice = getFareDisplayPrice(cheapest, showPerAdultPricing)
        if (price == null) return cheapest
        if (cheapestPrice == null) return fare
        return price < cheapestPrice ? fare : cheapest
    }, undefined)
}

/** Fare count per display cabin — prefers BE `cabinFareIds.length` when present. */
export const getCabinFareCount = (
    faresArray: CabinFareLike[] = [],
    cabinClassSummary?: CabinClassSummaryLike
): Partial<Record<CabinTabId, number>> => {
    const grouped = groupFaresByCabin(faresArray)
    const cabins = new Set<CabinTabId>([...Object.keys(grouped), ...Object.keys(cabinClassSummary || {})])
    const counts: Partial<Record<CabinTabId, number>> = {}
    cabins.forEach(cabin => {
        const beCount = cabinClassSummary?.[cabin]?.cabinFareIds?.length
        counts[cabin] =
            typeof beCount === 'number' ? beCount : filterFaresByCabin(faresArray, cabin, cabinClassSummary as CabinClassSummary).length
    })
    return counts
}

/**
 * Merge cabinClassSummary across a card's subTravelOptions. Unions cabinFareIds,
 * takes the lower minCabinPrice, keeps the first non-null cabinPromoBanner.
 * Iterates every key (including INDIGO_UPFRONT), not just CABIN_ORDER.
 */
export const mergeCabinClassSummaries = (
    summaries: Array<CabinClassSummary | null | undefined>
): CabinClassSummary | undefined => {
    const present = summaries.filter((s): s is CabinClassSummary => !!s && Object.keys(s).length > 0)
    if (!present.length) return undefined
    if (present.length === 1) return present[0]

    const allKeys = Array.from(new Set(present.flatMap(s => Object.keys(s))))
    const merged: CabinClassSummary = {}
    allKeys.forEach(cabin => {
        const entries = present
            .map(s => s[cabin as keyof CabinClassSummary])
            .filter(Boolean) as NonNullable<CabinClassSummary[CabinClassType]>[]
        if (!entries.length) return
        const cabinFareIds = Array.from(new Set(entries.flatMap(e => e.cabinFareIds || [])))
        const prices = entries.map(e => e.minCabinPrice).filter((p): p is number => typeof p === 'number')
        merged[cabin as CabinClassType] = {
            minCabinPrice: prices.length ? Math.min(...prices) : null,
            cabinFareIds,
            cabinPromoBanner: entries.find(e => e.cabinPromoBanner)?.cabinPromoBanner ?? null,
        }
    })
    return merged
}

/**
 * Cross-cabin RT rules use the cabin the fare actually flies in, not the tab —
 * Up Front and Economy are interchangeable across legs.
 */
export const getFareCabin = (fare?: CabinFareLike | null): FareCabinTabId | undefined =>
    resolveThemeCabin(fare)

/** True when the onward (index 0) and return (index 1) selected fares resolve to different *theme* cabins. */
export const isCrossCabinMismatch = (selectedFares: Array<CabinFareLike | null | undefined> = []): boolean => {
    const onwardFare = selectedFares?.[0]
    const returnFare = selectedFares?.[1]
    if (!onwardFare || !returnFare || Object.keys(onwardFare).length === 0 || Object.keys(returnFare).length === 0) {
        return false
    }
    const onwardCabin = getFareCabin(onwardFare)
    const returnCabin = getFareCabin(returnFare)
    if (!onwardCabin || !returnCabin) return false
    return onwardCabin !== returnCabin
}

/**
 * Default/repaired tab selection: requested cabin if still available, else ECONOMY
 * (so an Economy search never lands on Indigo Upfront), else the first available.
 */
export const getDefaultSelectedCabin = (
    requestedCabin: string | undefined,
    availableCabins: CabinTabId[]
): CabinTabId => {
    if (!availableCabins.length) return 'ECONOMY'
    const requested = normalizeCabinValue(requestedCabin)
    if (requested && availableCabins.includes(requested)) return requested
    return availableCabins.includes('ECONOMY') ? 'ECONOMY' : availableCabins[0]
}

export const getSingleCabinTabs = (cabin?: string): CabinTabId[] => [normalizeCabin(cabin) || 'ECONOMY']

/**
 * True only when `selectedCabin` ranks strictly above `searchedCabin` in
 * CABIN_ORDER — i.e. it's an upgrade. Unresolvable searched cabin falls back to
 * Economy (index 0). Operates on *theme* cabins.
 */
export const isCabinUpgradeOverSearched = (
    selectedCabin: string | undefined,
    searchedCabin: string | undefined
): boolean => {
    const selected = normalizeCabin(selectedCabin)
    if (!selected) return false
    const searched = normalizeCabin(searchedCabin)
    const searchedIndex = searched ? CABIN_ORDER.indexOf(searched) : 0
    return CABIN_ORDER.indexOf(selected) > searchedIndex
}

/**
 * Upgrade CTA target tab from `upgradeCallout.targetCabin`. Prefers an exact
 * match in available tabs (e.g. INDIGO_UPFRONT); never hardcodes Premium Economy.
 */
export const resolveUpgradeCalloutTab = (
    targetCabin: string | undefined | null,
    availableCabins: CabinTabId[] = []
): CabinTabId | undefined => {
    const normalized = normalizeCabinValue(targetCabin)
    if (!normalized) return undefined
    if (availableCabins.includes(normalized)) return normalized
    // Theme target with only a sub-product tab present — open the matching theme tab if any.
    const themeMatch = availableCabins.find(tab => getCabinThemeForTab([], tab) === normalized)
    return themeMatch
}
